import { Timetable } from '@schooly/api';

export type GroupConflict = { timetableId: string; timetableName: string };

export type PublishedTimetableGroups = {
  id: string;
  name: string;
  groupIds: string[];
};

export function selectOtherPublishedTimetables(
  timetables: Timetable[],
  currentStructureId: string,
  schoolYearId: string,
): Timetable[] {
  return timetables.filter(
    (timetable) =>
      timetable.published_at !== null &&
      timetable.structure_id !== currentStructureId &&
      timetable.school_year_id === schoolYearId,
  );
}

export function buildGroupConflictMap(
  publishedTimetables: PublishedTimetableGroups[],
): Map<string, GroupConflict> {
  const map = new Map<string, GroupConflict>();

  for (const timetable of publishedTimetables) {
    for (const groupId of timetable.groupIds) {
      if (!map.has(groupId)) {
        map.set(groupId, { timetableId: timetable.id, timetableName: timetable.name });
      }
    }
  }

  return map;
}

export function removeGroupsFromSlotAllocations<T extends { group_id: string }>(
  slotAllocations: Record<string, T[]>,
  groupIdsToRemove: Set<string>,
): Record<string, T[]> {
  const result: Record<string, T[]> = {};

  for (const [timeSlotId, allocations] of Object.entries(slotAllocations)) {
    result[timeSlotId] = allocations.filter(
      (allocation) => !groupIdsToRemove.has(allocation.group_id),
    );
  }

  return result;
}

export type ScheduledGroupConflict = { groupId: string; conflict: GroupConflict };

export function selectConflictingScheduledGroups(
  slotAllocations: Record<string, { group_id: string }[]>,
  conflictMap: Map<string, GroupConflict>,
): ScheduledGroupConflict[] {
  const seen = new Set<string>();
  const result: ScheduledGroupConflict[] = [];

  for (const allocations of Object.values(slotAllocations)) {
    for (const { group_id: groupId } of allocations) {
      if (seen.has(groupId)) continue;

      const conflict = conflictMap.get(groupId);
      if (conflict) {
        seen.add(groupId);
        result.push({ groupId, conflict });
      }
    }
  }

  return result;
}
